import { useCallback, useEffect, useMemo, useState, type CSSProperties } from "react";
import { Button, ConfirmDialog, SearchField, Select, TextField } from "@legends/ui";
import type { ColDef, ICellRendererParams } from "ag-grid-community";
import DataGrid from "./DataGrid";
import AdminModal from "./admin/AdminModal";
import { MultiSelectCellEditor, formatMultiSelect, type MultiSelectOption } from "./MultiSelectCellEditor";
import {
  createTenant,
  createUser,
  deleteUser,
  fetchAuthzUsers,
  fetchCaveAccess,
  fetchConfigurableCaves,
  fetchImpersonableViews,
  impersonate,
  impersonateView,
  setCaveAccess,
  setImpersonationPermission,
  setVIP,
  setLobbyPublishAccess,
  setRole,
  setViewImpersonation,
  updateUser,
  type AuthzUsersResponse,
  type Cave,
  type CaveAccessRow,
  type ImpersonableTenantViews,
  type Tenant,
  type ViewName,
} from "./api";
import { Section, alertStyle, errMsg, monoLabelStyle } from "./sections";

export interface SecurityScreenProps {
  /** The signed-in operator — their own row can't be impersonated or deleted. */
  currentUsername: string;
}

// Unit separator — joins a grant into one opaque multi-select token so a tenant id
// or role/view never collides with the delimiter.
const SEP = "␟";
const tok = (tenant: string, leaf: string) => `${tenant}${SEP}${leaf}`;
const untok = (t: string): [string, string] => {
  const i = t.indexOf(SEP);
  return i < 0 ? [t, ""] : [t.slice(0, i), t.slice(i + 1)];
};

/** Launch an impersonation in a fresh tab; this tab's session stays untouched (ADR 0007). */
function openImpersonationTab(token: string) {
  window.open(`/#imp=${encodeURIComponent(token)}`, "_blank");
}

// Compact in-grid action button (denser than the @legends/ui Button, sized to the grid).
function actionBtn(color: string): CSSProperties {
  return {
    background: "transparent",
    border: `1px solid ${color}55`,
    color,
    borderRadius: 4,
    padding: "1px 7px",
    fontSize: 11,
    lineHeight: 1.5,
    cursor: "pointer",
    whiteSpace: "nowrap",
  };
}

interface UserRow {
  username: string;
  displayName: string;
  email: string;
  superAdmin: boolean;
  impersonateAllowed: boolean;
  vip: boolean;
  roleTokens: string[];
  viewTokens: string[];
}
type CaveAccessRowUI = CaveAccessRow & { access: boolean; publish: boolean };

// Consolidated Security console (ADR 0157, LSC-302): the former Admin / Security /
// Impersonate / Cave-access tabs folded into ONE surface on the Cave-themed AG Grid
// (ADR 0156). One per-user grid carries identity + super-admin + tenant roles +
// impersonable views + impersonation, edited in-line (checkbox + multi-select
// popup editors); Impersonate is a row action. Tenants (create/list) and per-cave
// access are their own grids (different primitives). Multi-select edits diff the set
// and fan out to the existing single-grant PUTs — no bulk write endpoint (MVP).
export default function SecurityScreen({ currentUsername }: SecurityScreenProps) {
  const [data, setData] = useState<AuthzUsersResponse | null>(null);
  const [caves, setCaves] = useState<Cave[]>([]);
  const [impViews, setImpViews] = useState<ImpersonableTenantViews[]>([]);
  const [q, setQ] = useState("");
  // Per-grid quick-filters so Tenants + Cave-access match the Users grid (ADR 0207).
  const [tenantsQ, setTenantsQ] = useState("");
  const [accessQ, setAccessQ] = useState("");
  const [error, setError] = useState<string | null>(null);

  const [pendingDelete, setPendingDelete] = useState<UserRow | null>(null);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const [pwUser, setPwUser] = useState<string | null>(null);
  const [pwValue, setPwValue] = useState("");
  const [pwBusy, setPwBusy] = useState(false);
  // Impersonation now requires a reason (ADR 0160): a small modal collects it
  // before the parallel tab is launched.
  const [impUser, setImpUser] = useState<string | null>(null);
  const [impReason, setImpReason] = useState("");
  const [impBusy, setImpBusy] = useState(false);

  const reload = useCallback(async () => {
    try {
      setData(await fetchAuthzUsers());
    } catch (e) {
      setError(errMsg(e));
    }
  }, []);

  useEffect(() => {
    void reload();
    fetchConfigurableCaves()
      .then((cs) => setCaves(cs ?? []))
      .catch(() => {});
    fetchImpersonableViews()
      .then(setImpViews)
      .catch(() => {});
  }, [reload]);

  const tenants = data?.tenants ?? [];
  const grantableRoles = data?.grantableRoles ?? [];
  const views = data?.views ?? [];
  const multiTenant = tenants.length > 1;

  const roleOptions = useMemo<MultiSelectOption[]>(
    () => tenants.flatMap((t) => grantableRoles.map((role) => ({ value: tok(t.id, role), label: multiTenant ? `${role} · ${t.name}` : role }))),
    [tenants, grantableRoles, multiTenant],
  );
  const viewOptions = useMemo<MultiSelectOption[]>(
    () => tenants.flatMap((t) => views.map((v) => ({ value: tok(t.id, v), label: multiTenant ? `${v} · ${t.name}` : v }))),
    [tenants, views, multiTenant],
  );

  const userRows = useMemo<UserRow[]>(
    () =>
      (data?.users ?? []).map((u) => ({
        username: u.username,
        displayName: u.displayName ?? "",
        email: u.email ?? "",
        superAdmin: u.superAdmin,
        impersonateAllowed: u.impersonateAllowed,
        vip: u.vip,
        roleTokens: u.roles.map((r) => tok(r.tenant, r.role)),
        viewTokens: u.views.map((v) => tok(v.tenant, v.view)),
      })),
    [data],
  );

  const onEditUser = async (row: UserRow, field: string, value: unknown) => {
    const raw = data?.users.find((u) => u.username === row.username);
    if (!raw) return;
    setError(null);
    try {
      if (field === "displayName" || field === "email") {
        await updateUser(row.username, {
          displayName: field === "displayName" ? String(value ?? "") : raw.displayName ?? "",
          email: field === "email" ? String(value ?? "") : raw.email ?? "",
        });
      } else if (field === "superAdmin") {
        await setRole(row.username, "super_admin", "*", Boolean(value));
      } else if (field === "impersonateAllowed") {
        await setImpersonationPermission(row.username, Boolean(value));
      } else if (field === "vip") {
        await setVIP(row.username, Boolean(value));
      } else if (field === "roleTokens") {
        const before = new Set(raw.roles.map((r) => tok(r.tenant, r.role)));
        const after = new Set(value as string[]);
        for (const t of after) if (!before.has(t)) { const [tenant, role] = untok(t); await setRole(row.username, role, tenant, true); }
        for (const t of before) if (!after.has(t)) { const [tenant, role] = untok(t); await setRole(row.username, role, tenant, false); }
      } else if (field === "viewTokens") {
        const before = new Set(raw.views.map((v) => tok(v.tenant, v.view)));
        const after = new Set(value as string[]);
        for (const t of after) if (!before.has(t)) { const [tenant, view] = untok(t); await setViewImpersonation(row.username, tenant, view as ViewName, true); }
        for (const t of before) if (!after.has(t)) { const [tenant, view] = untok(t); await setViewImpersonation(row.username, tenant, view as ViewName, false); }
      }
      await reload();
    } catch (e) {
      setError(errMsg(e));
      await reload();
    }
  };

  // Add-user create modal (LSC-328): a clear, obvious flow that makes the auth mode
  // + password explicit at creation. "Password" = a local account that signs in with
  // credentials; "Google / Apple (SSO)" = no password — the user signs in with SSO,
  // and a password can be set later from the row action.
  const [nuUsername, setNuUsername] = useState("");
  const [nuName, setNuName] = useState("");
  const [nuEmail, setNuEmail] = useState("");
  const [nuMode, setNuMode] = useState<"password" | "sso">("password");
  const [nuPassword, setNuPassword] = useState("");
  // Optional role at creation (LSC-402): assign Director/Legend/etc. (or super-admin) in
  // the same form, so making an operator doesn't require a second trip to edit the grid.
  // "" = no role. Non-super roles carry a tenant (a tok(tenant,role) token from roleOptions).
  const [nuRole, setNuRole] = useState("");
  const [addBusy, setAddBusy] = useState(false);
  const resetAddUser = () => { setNuUsername(""); setNuName(""); setNuEmail(""); setNuMode("password"); setNuPassword(""); setNuRole(""); };

  const submitCreateUser = async (): Promise<boolean> => {
    if (!nuUsername.trim()) {
      setError("Username is required.");
      return false;
    }
    if (nuMode === "password" && nuPassword.length < 8) {
      setError("A password account needs a password of at least 8 characters.");
      return false;
    }
    setError(null);
    setAddBusy(true);
    try {
      await createUser({
        username: nuUsername.trim(),
        ...(nuName.trim() ? { displayName: nuName.trim() } : {}),
        ...(nuEmail.trim() ? { email: nuEmail.trim() } : {}),
        ...(nuMode === "password" ? { password: nuPassword } : {}),
      });
      // Grant the chosen role in the same step (LSC-402), so a new Director/Legend is
      // usable without a separate grid edit. super_admin is global (domain "*"); a tenant
      // role rides a tok(tenant,role) token.
      if (nuRole === "super_admin") {
        await setRole(nuUsername.trim(), "super_admin", "*", true);
      } else if (nuRole) {
        const [tenant, role] = untok(nuRole);
        await setRole(nuUsername.trim(), role, tenant, true);
      }
      resetAddUser();
      await reload();
      return true;
    } catch (e) {
      setError(errMsg(e));
      return false;
    } finally {
      setAddBusy(false);
    }
  };

  const askDeleteUser = (row: UserRow) => {
    if (row.username === currentUsername) {
      setError("You can't delete your own account.");
      return;
    }
    setDeleteError(null);
    setPendingDelete(row);
  };
  const confirmDeleteUser = async () => {
    if (!pendingDelete) return;
    setDeleteBusy(true);
    setDeleteError(null);
    try {
      await deleteUser(pendingDelete.username);
      setPendingDelete(null);
      await reload();
    } catch (e) {
      setDeleteError(errMsg(e));
    } finally {
      setDeleteBusy(false);
    }
  };

  const doImpersonate = (username: string) => {
    setError(null);
    setImpReason("");
    setImpUser(username);
  };
  const confirmImpersonate = async () => {
    if (!impUser || !impReason.trim()) return;
    setImpBusy(true);
    setError(null);
    try {
      const token = await impersonate(impUser, impReason.trim());
      openImpersonationTab(token);
      setImpUser(null);
      setImpReason("");
    } catch (e) {
      setError(errMsg(e));
    } finally {
      setImpBusy(false);
    }
  };

  const savePassword = async () => {
    if (!pwUser || !pwValue) return;
    setPwBusy(true);
    setError(null);
    try {
      await updateUser(pwUser, { password: pwValue });
      setPwUser(null);
      setPwValue("");
    } catch (e) {
      setError(errMsg(e));
    } finally {
      setPwBusy(false);
    }
  };

  const userColumns = useMemo<ColDef<UserRow>[]>(
    () => [
      { field: "username", headerName: "User", minWidth: 130, editable: false },
      { field: "displayName", headerName: "Name", minWidth: 120, editable: true },
      { field: "email", headerName: "Email", minWidth: 150, editable: true },
      { field: "superAdmin", headerName: "Super", width: 92, maxWidth: 100, editable: true, filter: false, cellEditor: "agCheckboxCellEditor", cellRenderer: "agCheckboxCellRenderer" },
      {
        field: "roleTokens",
        headerName: "Roles",
        minWidth: 150,
        editable: true,
        filter: false,
        sortable: false,
        cellEditor: MultiSelectCellEditor,
        cellEditorPopup: true,
        cellEditorParams: { options: roleOptions, emptyText: "No tenant roles" },
        valueFormatter: (p) => formatMultiSelect(p.value, roleOptions),
      },
      {
        field: "viewTokens",
        headerName: "Impersonable views",
        minWidth: 160,
        editable: true,
        filter: false,
        sortable: false,
        cellEditor: MultiSelectCellEditor,
        cellEditorPopup: true,
        cellEditorParams: { options: viewOptions, emptyText: "No views" },
        valueFormatter: (p) => formatMultiSelect(p.value, viewOptions),
      },
      { field: "impersonateAllowed", headerName: "May impersonate", width: 130, maxWidth: 150, editable: true, filter: false, cellEditor: "agCheckboxCellEditor", cellRenderer: "agCheckboxCellRenderer" },
      { field: "vip", headerName: "VIP", width: 90, maxWidth: 110, editable: true, filter: false, cellEditor: "agCheckboxCellEditor", cellRenderer: "agCheckboxCellRenderer" },
      {
        colId: "userActions",
        headerName: "",
        width: 168,
        minWidth: 168,
        maxWidth: 176,
        pinned: "right",
        sortable: false,
        filter: false,
        editable: false,
        resizable: false,
        cellStyle: { display: "flex", alignItems: "center", gap: 5, justifyContent: "flex-end", paddingRight: 4 },
        cellRenderer: (p: ICellRendererParams<UserRow>) => {
          if (p.node.rowPinned === "top" || !p.data) return null;
          const self = p.data.username === currentUsername;
          return (
            <>
              {!self && (
                <button type="button" aria-label={`Impersonate ${p.data.username}`} onClick={() => doImpersonate(p.data!.username)} style={actionBtn("#c9a227")}>
                  Impersonate
                </button>
              )}
              <button type="button" aria-label={`Set password for ${p.data.username}`} onClick={() => { setPwUser(p.data!.username); setPwValue(""); }} style={actionBtn("#a49c90")}>
                Password
              </button>
            </>
          );
        },
      },
    ],
    [roleOptions, viewOptions, currentUsername],
  );

  // ── Tenants ───────────────────────────────────────────────────────────────
  const tenantColumns = useMemo<ColDef<Tenant>[]>(
    () => [
      { field: "id", headerName: "Tenant ID", minWidth: 140, editable: false, cellStyle: () => ({ fontFamily: "var(--font-mono)", color: "var(--accent-hi)" }) },
      { field: "name", headerName: "Name", minWidth: 160, editable: false },
    ],
    [],
  );
  const onAddTenant = async (draft: Tenant) => {
    if (!draft.id?.trim() || !draft.name?.trim()) {
      setError("Tenant id and name are required.");
      return false;
    }
    setError(null);
    try {
      await createTenant(draft.id.trim(), draft.name.trim());
      await reload();
    } catch (e) {
      setError(errMsg(e));
      return false;
    }
  };

  // ── Cave access ───────────────────────────────────────────────────────────
  const [caveId, setCaveId] = useState<string>("");
  const [accessRows, setAccessRows] = useState<CaveAccessRowUI[]>([]);
  const [canDelegatePublish, setCanDelegatePublish] = useState(false);
  useEffect(() => {
    if (!caveId && caves.length) setCaveId(caves[0].id);
  }, [caves, caveId]);
  const loadAccess = useCallback(async (id: string) => {
    if (!id) return;
    try {
      const r = await fetchCaveAccess(id);
      setAccessRows(r.access.map((a) => ({ ...a, access: a.viaRole || a.direct, publish: a.canPublish })));
      setCanDelegatePublish(r.canDelegatePublish);
    } catch (e) {
      setError(errMsg(e));
    }
  }, []);
  useEffect(() => {
    void loadAccess(caveId);
  }, [caveId, loadAccess]);

  const onEditAccess = async (row: CaveAccessRowUI, field: string, value: unknown) => {
    setError(null);
    try {
      if (field === "access") await setCaveAccess(caveId, row.username, Boolean(value));
      else if (field === "publish") await setLobbyPublishAccess(caveId, row.username, Boolean(value));
      await loadAccess(caveId);
    } catch (e) {
      setError(errMsg(e));
      await loadAccess(caveId);
    }
  };
  const accessColumns = useMemo<ColDef<CaveAccessRowUI>[]>(
    () => [
      { field: "username", headerName: "User", minWidth: 130, editable: false },
      { field: "displayName", headerName: "Name", minWidth: 120, editable: false, valueFormatter: (p) => String(p.value ?? "") },
      { colId: "roles", headerName: "Roles", minWidth: 130, editable: false, sortable: false, valueGetter: (p) => (p.data?.roles ?? []).join(", ") },
      { field: "access", headerName: "Access", width: 110, maxWidth: 120, filter: false, editable: (p) => !p.data?.viaRole, cellEditor: "agCheckboxCellEditor", cellRenderer: "agCheckboxCellRenderer" },
      { colId: "accessVia", headerName: "", width: 90, maxWidth: 100, sortable: false, filter: false, editable: false, valueGetter: (p) => (p.data?.viaRole ? "via role" : ""), cellStyle: () => ({ color: "var(--text-dim)", fontSize: 11 }) },
      { field: "publish", headerName: "Publish", width: 110, maxWidth: 120, filter: false, editable: (p) => !p.data?.publishViaRole && canDelegatePublish, cellEditor: "agCheckboxCellEditor", cellRenderer: "agCheckboxCellRenderer" },
      { colId: "publishVia", headerName: "", width: 90, maxWidth: 100, sortable: false, filter: false, editable: false, valueGetter: (p) => (p.data?.publishViaRole ? "via role" : ""), cellStyle: () => ({ color: "var(--text-dim)", fontSize: 11 }) },
    ],
    [canDelegatePublish],
  );

  return (
    <div style={{ display: "grid", gap: 18 }}>
      <div>
        <h1 style={{ margin: 0, ...monoLabelStyle, fontWeight: 600 }}>Security</h1>
        <p style={{ margin: "10px 0 0", fontSize: "var(--fs-body)", color: "var(--text-dim)" }}>
          Users, roles, impersonation, tenants, and per-cave access in one place. Edit a user's identity, super-admin
          grant, tenant roles, impersonable views, and impersonation permission in-line. Add a user with{" "}
          <strong>＋ Add a user</strong>; each section below can be folded away to focus.
        </p>
      </div>

      {error && (
        <p role="alert" style={alertStyle}>
          {error}
        </p>
      )}

      <Section title="Users" collapsible count={userRows.length}>
        <div style={{ margin: "12px 0 10px", display: "flex", alignItems: "center", gap: 12 }}>
          <SearchField value={q} onChange={setQ} placeholder="Filter users…" width={320} />
          <span style={{ ...monoLabelStyle, marginLeft: "auto" }}>{userRows.length} users</span>
        </div>
        <DataGrid<UserRow>
          rows={userRows}
          columns={userColumns}
          getRowId={(r) => r.username}
          onEdit={onEditUser}
          onDelete={askDeleteUser}
          addModal
          addLabel="a user"
          renderAddModal={({ close }) => (
            <AdminModal
              title="Add a user"
              confirmLabel={addBusy ? "Creating…" : "Create user"}
              busy={addBusy}
              onConfirm={() => void submitCreateUser().then((ok) => { if (ok) close(); })}
              onCancel={() => { if (!addBusy) { resetAddUser(); setError(null); close(); } }}
            >
              <div style={{ display: "grid", gap: 12, minWidth: 300 }}>
                <TextField label="Username" name="new-username" value={nuUsername} onChange={setNuUsername} placeholder="e.g. jordan" />
                <TextField label="Name (optional)" name="new-name" value={nuName} onChange={setNuName} placeholder="Jordan Rivera" />
                <TextField label="Email (optional)" name="new-email" type="email" value={nuEmail} onChange={setNuEmail} placeholder="jordan@example.com" />
                <Select
                  label="Role (optional)"
                  aria-label="Role"
                  value={nuRole}
                  onChange={setNuRole}
                  options={[
                    { value: "", label: "No role (assign later)" },
                    { value: "super_admin", label: "Super admin" },
                    ...roleOptions,
                  ]}
                />
                <Select
                  label="How they sign in"
                  aria-label="How they sign in"
                  value={nuMode}
                  onChange={(v) => setNuMode(v as "password" | "sso")}
                  options={[
                    { value: "password", label: "Password (this admin sets it)" },
                    { value: "sso", label: "Google / Apple (no password)" },
                  ]}
                />
                {nuMode === "password" ? (
                  <div>
                    <TextField label="Password" name="new-password" type="password" value={nuPassword} onChange={setNuPassword} placeholder="at least 8 characters" />
                    <span style={{ display: "block", marginTop: 4, fontSize: "var(--fs-xs)", color: "var(--text-dim)" }}>
                      They sign in with this username and password. You can change it later from the user's Password action.
                    </span>
                  </div>
                ) : (
                  <span style={{ fontSize: "var(--fs-xs)", color: "var(--text-dim)" }}>
                    No password is set — they sign in with Google or Apple. You can add a password later from the user's Password action.
                  </span>
                )}
                {error && <span role="alert" style={{ fontSize: "var(--fs-sm)", color: "var(--live, #e8735a)" }}>{error}</span>}
              </div>
            </AdminModal>
          )}
          paginated
          quickFilterText={q}
          label="Users and access"
          emptyText="No users."
        />
        {pwUser && (
          <form
            aria-label={`Set password for ${pwUser}`}
            onSubmit={(e) => {
              e.preventDefault();
              void savePassword();
            }}
            style={{ marginTop: 12, display: "flex", alignItems: "flex-end", gap: 10, flexWrap: "wrap" }}
          >
            <TextField label={`New password for ${pwUser}`} name="set-password" type="password" value={pwValue} onChange={setPwValue} />
            <Button variant="accent" type="submit" disabled={pwBusy || !pwValue}>
              {pwBusy ? "Saving…" : "Save password"}
            </Button>
            <Button variant="muted" onClick={() => { setPwUser(null); setPwValue(""); }}>
              Cancel
            </Button>
          </form>
        )}
        {impUser && (
          <form
            aria-label={`Impersonate ${impUser}`}
            onSubmit={(e) => {
              e.preventDefault();
              void confirmImpersonate();
            }}
            style={{ marginTop: 12, display: "flex", alignItems: "flex-end", gap: 10, flexWrap: "wrap" }}
          >
            <TextField
              label={`Reason to impersonate ${impUser} (30-min session, audited)`}
              name="impersonate-reason"
              value={impReason}
              onChange={setImpReason}
              placeholder="e.g. support ticket #123"
            />
            <Button variant="accent" type="submit" disabled={impBusy || !impReason.trim()}>
              {impBusy ? "Opening…" : "Impersonate"}
            </Button>
            <Button variant="muted" onClick={() => { setImpUser(null); setImpReason(""); }}>
              Cancel
            </Button>
          </form>
        )}
      </Section>

      <Section title="Tenants" collapsible count={tenants.length}>
        <div style={{ marginTop: 12 }}>
          <div style={{ margin: "0 0 10px" }}>
            <SearchField value={tenantsQ} onChange={setTenantsQ} placeholder="Search tenants…" aria-label="Search tenants" width={280} />
          </div>
          <DataGrid<Tenant>
            rows={tenants}
            columns={tenantColumns}
            getRowId={(t) => t.id}
            onAdd={onAddTenant}
            addTemplate={{ id: "", name: "" }}
            quickFilterText={tenantsQ}
            label="Tenants"
            emptyText="No tenants."
          />
        </div>
      </Section>

      <Section title="Cave access" collapsible count={caves.length}>
        {caves.length === 0 ? (
          <p style={{ margin: "12px 0 0", fontSize: "var(--fs-xs)", color: "var(--text-dim)" }}>No caves.</p>
        ) : (
          <>
            <div style={{ margin: "12px 0", display: "flex", alignItems: "flex-end", gap: 12, flexWrap: "wrap" }}>
              <div style={{ minWidth: 260 }}>
                <Select
                  label="Cave"
                  aria-label="Cave"
                  value={caveId}
                  onChange={setCaveId}
                  options={caves.map((c) => ({ value: c.id, label: c.name }))}
                />
              </div>
              <SearchField value={accessQ} onChange={setAccessQ} placeholder="Search users…" aria-label="Search cave access" width={280} />
            </div>
            <DataGrid<CaveAccessRowUI>
              rows={accessRows}
              columns={accessColumns}
              getRowId={(r) => r.username}
              onEdit={onEditAccess}
              quickFilterText={accessQ}
              label="Cave access"
              emptyText="No users for this cave."
            />
            {!canDelegatePublish && (
              <p style={{ margin: "8px 0 0", fontSize: "var(--fs-xs)", color: "var(--text-dim)" }}>
                Publish delegation is admin-only for this cave.
              </p>
            )}
          </>
        )}
      </Section>

      {impViews.length > 0 && (
        <Section title="Preview a persona view" collapsible>
          <p style={{ margin: "12px 0 6px", fontSize: "var(--fs-xs)", color: "var(--text-dim)" }}>
            Open a tenant's persona view in a new tab (your session stays as you).
          </p>
          {impViews.map((entry) => (
            <div key={entry.tenant.id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "6px 0", flexWrap: "wrap" }}>
              <span style={{ ...monoLabelStyle, minWidth: 120 }}>{entry.tenant.name}</span>
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                {entry.views.map((view) => (
                  <Button
                    key={view}
                    size="sm"
                    aria-label={`Preview ${view} view of ${entry.tenant.id}`}
                    onClick={() => {
                      setError(null);
                      impersonateView(entry.tenant.id, view).then(openImpersonationTab).catch((e: unknown) => setError(errMsg(e)));
                    }}
                    style={{ textTransform: "uppercase" }}
                  >
                    {view}
                  </Button>
                ))}
              </div>
            </div>
          ))}
        </Section>
      )}

      {pendingDelete && (
        <ConfirmDialog
          title={`Delete ${pendingDelete.username}?`}
          destructive
          confirmLabel="Delete user"
          busy={deleteBusy}
          error={deleteError ?? undefined}
          onConfirm={() => void confirmDeleteUser()}
          onCancel={() => { if (!deleteBusy) { setPendingDelete(null); setDeleteError(null); } }}
        >
          This permanently deletes <strong>{pendingDelete.username}</strong> and their grants. This cannot be undone.
        </ConfirmDialog>
      )}
    </div>
  );
}
